import { useEffect, useRef, useState } from "react";
import { restPost } from "./restClient.js";
import { flattenInventoryRow } from "./inventoryRows.js";
import { getDirectoryUser, getEndpointUserAssignments } from "./identityClient.js";
import { subjectKey } from "./escalationClient.js";

const MAX_LOOKUPS_PER_PASS = 24;

const _names = new Map();
const _inflight = new Map();

function lookup(endpointId) {
    if (_names.has(endpointId)) return Promise.resolve(_names.get(endpointId));
    const existing = _inflight.get(endpointId);
    if (existing) return existing;
    const request = restPost("/v1/endpoints/metadata:get", { endpoint_id: endpointId })
        .then((resp) => {
            const row = resp?.metadata ? flattenInventoryRow(resp.metadata) : null;
            const name = row?.hostname
                ? { hostname: row.hostname, registeredUser: row.registeredUser }
                : null;
            _names.set(endpointId, name);
            return name;
        })
        .catch(() => {
            _names.set(endpointId, null);
            return null;
        })
        .finally(() => { _inflight.delete(endpointId); });
    _inflight.set(endpointId, request);
    return request;
}

// Keyed on the joined ids rather than the array, whose identity changes on
// every render of a list component.
export function useEndpointNames(endpointIds, enabled = true) {
    const key = endpointIds.filter(Boolean).join(",");
    const [names, setNames] = useState(() => new Map());
    useEffect(() => {
        if (!enabled || !key) return undefined;
        const wanted = [...new Set(key.split(","))].slice(0, MAX_LOOKUPS_PER_PASS);
        let cancelled = false;
        Promise.all(wanted.map(lookup)).then(() => {
            if (cancelled) return;
            const next = new Map();
            for (const id of wanted) {
                const name = _names.get(id);
                if (name) next.set(id, name);
            }
            setNames(next);
        });
        return () => { cancelled = true; };
    }, [key, enabled]);
    return names;
}

const _people = new Map();
const _peopleInflight = new Map();

function lookupPerson(subject) {
    const key = subjectKey(subject);
    if (_people.has(key)) return Promise.resolve(_people.get(key));
    const existing = _peopleInflight.get(key);
    if (existing) return existing;
    const request = getDirectoryUser(subject.sourceProvider, subject.sourceId)
        .then((detail) => {
            const name = detail?.displayName || detail?.email || "";
            _people.set(key, name || null);
            return name || null;
        })
        .catch(() => {
            _people.set(key, null);
            return null;
        })
        .finally(() => { _peopleInflight.delete(key); });
    _peopleInflight.set(key, request);
    return request;
}

/**
 * Names for the people a request targets. A request carries the directory
 * identity and no name, because the subject key it was derived from cannot be
 * read back into one, so an approver would otherwise decide against a provider
 * user id. Keyed by `subjectKey`, one lookup per distinct person.
 */
export function useSubjectNames(subjects, enabled = true) {
    const key = subjects.map(subjectKey).filter(Boolean).join("|");
    const [names, setNames] = useState(() => new Map());
    // The array is rebuilt on every render of a list; `key` is its identity.
    const latest = useRef(subjects);
    latest.current = subjects;
    useEffect(() => {
        if (!enabled || !key) return undefined;
        const seen = new Map();
        for (const subject of latest.current) {
            const id = subjectKey(subject);
            if (id && !seen.has(id)) seen.set(id, subject);
        }
        const wanted = [...seen.values()].slice(0, MAX_LOOKUPS_PER_PASS);
        let cancelled = false;
        Promise.all(wanted.map(lookupPerson)).then(() => {
            if (cancelled) return;
            const next = new Map();
            for (const subject of wanted) {
                const id = subjectKey(subject);
                const name = _people.get(id);
                if (name) next.set(id, name);
            }
            setNames(next);
        });
        return () => { cancelled = true; };
    }, [key, enabled]);
    return names;
}

const _owners = new Map();
const _ownersInflight = new Map();
const _personEmails = new Map();
const _personEmailsInflight = new Map();

const normalizeEmail = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "");

function lookupOwnerEmails(endpointId) {
    if (_owners.has(endpointId)) return Promise.resolve(_owners.get(endpointId));
    const existing = _ownersInflight.get(endpointId);
    if (existing) return existing;
    const request = getEndpointUserAssignments(endpointId)
        .then((resp) => {
            const emails = new Set(
                (resp?.assignments ?? [])
                    .map((a) => normalizeEmail(a.userEmail ?? a.user_email))
                    .filter(Boolean),
            );
            _owners.set(endpointId, emails);
            return emails;
        })
        .catch(() => {
            _owners.set(endpointId, new Set());
            return new Set();
        })
        .finally(() => { _ownersInflight.delete(endpointId); });
    _ownersInflight.set(endpointId, request);
    return request;
}

function lookupPersonEmail(subject) {
    const key = subjectKey(subject);
    if (_personEmails.has(key)) return Promise.resolve(_personEmails.get(key));
    const existing = _personEmailsInflight.get(key);
    if (existing) return existing;
    const request = getDirectoryUser(subject.sourceProvider, subject.sourceId)
        .then((detail) => {
            const email = normalizeEmail(detail?.email);
            _personEmails.set(key, email);
            return email;
        })
        .catch(() => {
            _personEmails.set(key, "");
            return "";
        })
        .finally(() => { _personEmailsInflight.delete(key); });
    _personEmailsInflight.set(key, request);
    return request;
}

/**
 * The person-shaped escalation covering this machine, if one does.
 *
 * A person-shaped window reaches its target's activity on every machine they
 * own, so a machine they own is partly covered even though no escalation names
 * it. Matched on email: an assignment carries no provider user id, and the
 * subject key cannot be read back into one.
 */
export function useEscalationCoveringEndpoint(endpointId, subjects, enabled = true) {
    const key = subjects.map(subjectKey).filter(Boolean).join("|");
    const latest = useRef(subjects);
    latest.current = subjects;
    const [covering, setCovering] = useState(null);
    useEffect(() => {
        if (!enabled || !endpointId || !key) {
            setCovering(null);
            return undefined;
        }
        let cancelled = false;
        const wanted = latest.current.filter((s) => subjectKey(s)).slice(0, MAX_LOOKUPS_PER_PASS);
        Promise.all([
            lookupOwnerEmails(endpointId),
            Promise.all(wanted.map(lookupPersonEmail)),
        ]).then(([owners, emails]) => {
            if (cancelled) return;
            const index = emails.findIndex((email) => email && owners.has(email));
            setCovering(index === -1 ? null : wanted[index]);
        });
        return () => { cancelled = true; };
    }, [endpointId, key, enabled]);
    return covering;
}
